// TestThankYouScreen.tsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Mail, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { getTestProgression } from 'api/apiHelper';

interface TestThankYouScreenProps {
  onContinueToVideoTest?: () => void;
  isUnifiedFlow?: boolean;
  initialTestToken?: string;
}

const TestThankYouScreen: React.FC<TestThankYouScreenProps> = ({ 
  onContinueToVideoTest,
  isUnifiedFlow = false,
  initialTestToken
}) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = initialTestToken || paramTestToken;
  const [checkingVideoTest, setCheckingVideoTest] = useState<boolean>(false);

  useEffect(() => {
    // Optional: Log completion or send analytics
    console.log('Test completed with token:', testToken);
  }, [testToken]);

  const handleContinueToVideoTest = async () => {
    if (onContinueToVideoTest) {
      // If callback provided (unified flow), use it
      onContinueToVideoTest();
      return;
    }

    // Otherwise, check if we're in unified flow by checking URL
    if (window.location.pathname.includes('/unified-test/')) {
      setCheckingVideoTest(true);
      const initialToken = window.location.pathname.split('/unified-test/')[1];
      
      // Wait for video test session to be created (with retries)
      let retries = 0;
      const maxRetries = 10;
      
      const checkForVideoTest = async (): Promise<void> => {
        try {
          const response = await getTestProgression(initialToken);
          const progression = response.data;
          
          if (progression.video_test) {
            // Video test created, navigate to unified flow video welcome
            window.location.href = `/unified-test/${initialToken}`;
          } else if (retries < maxRetries) {
            retries++;
            setTimeout(checkForVideoTest, 1000);
          } else {
            toast.error('Video test is being prepared. Please wait a moment and refresh the page.');
            setCheckingVideoTest(false);
          }
        } catch (error: any) {
          console.error('Error checking for video test:', error);
          toast.error('Failed to proceed to video test. Please try again.');
          setCheckingVideoTest(false);
        }
      };
      
      checkForVideoTest();
    } else {
      // Not in unified flow, navigate to website
      window.location.href = "https://telerelation.com/";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12 text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>

          {/* Main Heading */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Test Submitted Successfully!
          </h1>

          {/* Thank You Message */}
          <p className="text-xl text-gray-700 mb-8">
            Thank you for completing the assessment.
          </p>

          {/* Information Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-indigo-50 rounded-lg p-6">
              <div className="flex items-center justify-center mb-3">
                <Mail className="w-8 h-8 text-brand-500" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">
                {isUnifiedFlow || window.location.pathname.includes('/unified-test/') ? 'Next: Video Interview' : "We'll Be In Touch"}
              </h3>
              <p className="text-sm text-gray-600">
                {isUnifiedFlow || window.location.pathname.includes('/unified-test/') 
                  ? 'Record video responses to 3 questions'
                  : 'The Telerelation Team will reach out to you shortly'}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center justify-center mb-3">
                <Clock className="w-8 h-8 text-brand-500" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">What's Next?</h3>
              <p className="text-sm text-gray-600">
                {isUnifiedFlow || window.location.pathname.includes('/unified-test/')
                  ? 'You\'ll need a camera and microphone ready'
                  : 'Our team is reviewing your responses and will contact you soon'}
              </p>
            </div>
          </div>

          {/* Detailed Message */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <p className="text-gray-700 leading-relaxed">
              Your test has been successfully submitted and our recruitment team has been notified. 
              We appreciate the time you've taken to complete this assessment.
            </p>
            {(isUnifiedFlow || window.location.pathname.includes('/unified-test/')) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-gray-700 leading-relaxed font-medium mb-2">
                  Next Step: Video Interview Test
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  In the next section, you'll be asked to record video responses to questions. 
                  Make sure you have a working camera and microphone, and find a quiet, well-lit environment. 
                  You'll have time to prepare your answers before recording each response.
                </p>
              </div>
            )}
          </div>

          {/* Contact Information (Optional) */}
          <div className="border-t pt-6">
            <p className="text-sm text-gray-500">
              If you have any questions, please don't hesitate to contact us at{' '}
              <a href="https://mail.google.com/mail/?view=cm&to=hr@telerelation.com" className="text-brand-500 hover:text-brand-600 font-medium">
                hr@telerelation.com
              </a>
            </p>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinueToVideoTest}
            disabled={checkingVideoTest}
            className="mt-6 px-8 py-3 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {checkingVideoTest ? (
              <>
                <Clock className="w-5 h-5 animate-spin" />
                Preparing Video Test...
              </>
            ) : isUnifiedFlow || window.location.pathname.includes('/unified-test/') ? (
              <>
                Continue to Video Test
                <ArrowRight className="w-5 h-5" />
              </>
            ) : (
              <>
                Go to Website
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestThankYouScreen;